import FormSelect from 'components/Select/FormSelect';
import Text from 'components/Text/Text';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { IItemForGetShortDishInfo } from 'types';
import { Checkbox } from 'antd';
import SearchField from 'components/SearchField/SearchField';
import ChunkOfTextWithCopying from 'components/Text/ChunkOfTextWithCopying';
import style from './MultipleSelect.module.scss';

interface IMultipleSelect {
	data: IItemForGetShortDishInfo[];
	isLoading: boolean;
	refetch: () => void;
	responseError: boolean;
	setSuggestedDishes: (value: string[]) => void;
	selectedSuggestedDishes: string[];
	selectedSuggestedDishSubCategory: Nullable<string>;
}

const MultipleSelect: React.FC<IMultipleSelect> = ({
	data,
	isLoading,
	refetch,
	responseError,
	selectedSuggestedDishSubCategory,
	selectedSuggestedDishes,
	setSuggestedDishes,
}) => {
	const [searchString, setSearchString] = useState<string>('');

	const filteredDataWithAdditionalContent = useMemo(() => {
		const updatedData = data.map((el) => ({
			...el,
			additionalContent: (
				<>{el.externalId && <ChunkOfTextWithCopying text={el.externalId} className={style.externalId} />}</>
			),
		}));

		if (searchString) {
			const filteredDataByName = updatedData.filter((el) =>
				el.name.toLowerCase().includes(searchString.toLowerCase()),
			);
			const filteredDataByExternalId = updatedData.filter((el) =>
				el.externalId?.toLowerCase().includes(searchString.toLowerCase()),
			);
			const unionFilteredData = Array.from(new Set([...filteredDataByName, ...filteredDataByExternalId]));
			return unionFilteredData;
		}
		return updatedData;
	}, [searchString, data]);

	const handleSelectAllData = useCallback(() => {
		const isCheckedAllItems = data.every((el) => selectedSuggestedDishes.includes(el.id));
		const dataIDs = data.map((el) => el.id);
		if (isCheckedAllItems) {
			setSuggestedDishes(selectedSuggestedDishes.filter((el) => !dataIDs.includes(el)));
		} else {
			const filteredData = data.map((el) => el.id).filter((el) => !selectedSuggestedDishes.includes(el));
			setSuggestedDishes([...selectedSuggestedDishes, ...filteredData]);
		}
	}, [data, selectedSuggestedDishes, setSuggestedDishes]);

	useEffect(() => {
		setSearchString('');
	}, [selectedSuggestedDishSubCategory]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => {
			const isChecked = () => {
				return data.every((el) => selectedSuggestedDishes.includes(el.id));
			};

			return (
				<>
					<div className={style.selectMenuHeader}>
						<Checkbox
							className={style.checkBox}
							checked={!!(data.length && isChecked())}
							disabled={!data.length}
							onChange={handleSelectAllData}>
							<Text translationNameSpace="form" translationKey="selectAllDishesLabel" asFragment={true} />
						</Checkbox>
						<SearchField
							onChangeSelectSearchString={setSearchString}
							searchString={searchString}
							className={style.searchField}
						/>
					</div>
					{menu}
				</>
			);
		},
		[data, selectedSuggestedDishes, handleSelectAllData, setSearchString, searchString],
	);

	return (
		<FormSelect
			name="selectedSuggestedDishes"
			label="dishesLabel"
			options={filteredDataWithAdditionalContent}
			fetchError={responseError}
			loading={isLoading}
			onRefetch={refetch}
			disabled={!selectedSuggestedDishSubCategory || isLoading}
			onChange={setSuggestedDishes}
			counterValue={selectedSuggestedDishes.length}
			dropdownRender={renderSelectMenu}
			mode="multiple"
		/>
	);
};

export default React.memo(MultipleSelect);
